/* eslint-disable @typescript-eslint/no-empty-function */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { extractErrors } from '@vsolv/dev-kit/ngx';
import { PropertySet } from '@vsolv/packages/properties/domain';
import { Observable, Subject, firstValueFrom, isObservable, takeUntil } from 'rxjs';
import { DIMENSION_ELEMENT_LOADER, DimensionElement, DimensionElementLoader } from '../../constants';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'vs-dimension-input-form-field',
  templateUrl: './dimension-input-form-field.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: DimensionInputFormFieldComponent,
    },
    { provide: NG_VALIDATORS, useExisting: DimensionInputFormFieldComponent, multi: true },
  ],
})
export class DimensionInputFormFieldComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(@Optional() @Inject(DIMENSION_ELEMENT_LOADER) private elementLoader: DimensionElementLoader | null) {}

  @Input() dimension!: PropertySet.DimensionModel;
  @Input() required = false;
  @Input() hidden = false;
  @Input() showHidden = false;
  @Input() order = 0;

  formControl = new FormControl<string | number | null>(null);
  elements: DimensionElement[] = [];
  isLoading = false;

  private destroy$ = new Subject<void>();

  async ngOnInit() {
    if (this.required) {
      this.formControl.setValidators(Validators.required);
      this.formControl.updateValueAndValidity();
    }

    if (!this.dimension.isScalar && this.elementLoader) {
      this.isLoading = true;
      try {
        const result = this.elementLoader(this.dimension.id);
        this.elements = isObservable(result)
          ? await firstValueFrom(result as Observable<DimensionElement[]>)
          : await (result as Promise<DimensionElement[]>);
      } catch (err) {
        console.error(`Failed to load elements for dimension "${this.dimension.id}":`, err);
        this.elements = [];
      } finally {
        this.isLoading = false;
        // Elements resolve after await; flush so vs-select's optionHTML can resolve the
        // selected label. Without this the trigger shows "undefined" until interaction.
        this.detectChangesSafe();
      }
    }

    this.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(v => this.onChange(v));
  }

  /** Safe CD after async work — view may already be destroyed if the dialog closed. */
  private detectChangesSafe() {
    try {
      this.cdr.detectChanges();
    } catch {
      // no-op
    }
  }

  validate(): ValidationErrors | null {
    return extractErrors(this.formControl);
  }

  writeValue(value: string | number | null): void {
    this.formControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: (v: string | number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.formControl.disable() : this.formControl.enable();
  }

  onChange = (_v: string | number | null) => {};
  onTouched = () => {};

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
